import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, first, map, Observable } from 'rxjs';
import { ParamsHelper } from 'src/app/helpers/ParamsHelper';
import { formatFilterDate } from 'src/app/pages/operation/shared/operation-filter.utils';
import { HandleErrorService } from 'src/app/services/handle-error/handle-error.service';
import { CarbonModalService } from 'src/app/services/modal/carbon-modal.service';
import { Pagination } from 'src/app/shared/interfaces/pagination';
import {
  PositionCustody,
  PositionCustodyResponse,
  PositionCustodyTotalResponse,
} from 'src/app/shared/interfaces/position-custody';
import { AppConfigService } from 'src/environments/app-config.service';

import { PositionCustodyFilter } from '../interfaces/position-custody-filter';
import { PositionConsultRequestOptions } from '../shared/position-consult-request.options';

@Injectable({
  providedIn: 'root',
})
export class PositionConsultService {
  private readonly environment: any;
  private readonly apiUrl: string;

  filters: BehaviorSubject<PositionCustodyFilter> = new BehaviorSubject({} as PositionCustodyFilter);

  constructor(
    private readonly httpClient: HttpClient,
    private readonly appConfig: AppConfigService,
    private readonly handleErrorService: HandleErrorService,
    private readonly carbonModalService: CarbonModalService,
  ) {
    this.environment = this.appConfig.config;
    this.apiUrl = `${this.environment.apiCustody}/custodies`;
  }

  getPosition(
    filters: PositionCustodyFilter,
    pagination: Pagination<PositionCustody>,
    options?: PositionConsultRequestOptions,
  ): Observable<Pagination<PositionCustody>> {
    const params = this.buildParams(filters, pagination);

    return this.httpClient.get<PositionCustodyResponse>(this.apiUrl, { params }).pipe(
      first(),
      map((res) => {
        pagination.results = this.extractCustodyResults(res?.data);
        pagination.total = res.meta?.totalRecords ?? pagination.results.length;
        pagination.totalPages = res.meta?.totalPages;
        return pagination;
      }),
      catchError((err) => this.handleConsultError(err, options)),
    );
  }

  /** Aceita `data` como array (contrato oficial) e ignora envelopes inesperados. */
  private extractCustodyResults(data: unknown): PositionCustody[] {
    if (Array.isArray(data)) {
      return data as PositionCustody[];
    }

    return [];
  }

  getCustodyTotal(): Observable<number> {
    return this.httpClient.get<PositionCustodyTotalResponse>(`${this.apiUrl}/total`).pipe(
      first(),
      map((response) => response.data?.totalTons ?? 0),
    );
  }

  private buildParams(
    filters: PositionCustodyFilter,
    pagination: Pagination<PositionCustody>,
  ): HttpParams {
    const helper = new ParamsHelper(new HttpParams())
      .setParams('page', pagination.page + '')
      .setParams('page-size', pagination.size + '');

    const positionDate = formatFilterDate(filters.positionDate);
    if (positionDate) {
      helper.setParams('positionDate', positionDate);
    }

    if (filters.accountCode) {
      helper.setParams('accountCode', filters.accountCode);
    }

    if (filters.carbonCreditCode) {
      helper.setParams('carbonCreditCode', filters.carbonCreditCode);
    }

    if (filters.projectId) {
      helper.setParams('projectId', filters.projectId);
    }

    if (filters.participantSimplifiedName) {
      helper.setParams('participantSimplifiedName', filters.participantSimplifiedName);
    }

    if (
      filters.financialInstrumentCode !== null
      && filters.financialInstrumentCode !== undefined
      && `${filters.financialInstrumentCode}`.trim() !== ''
    ) {
      helper.setParams('financialInstrumentCode', `${filters.financialInstrumentCode}`.trim());
    }

    return helper.getParams();
  }

  private handleConsultError(err: unknown, options?: PositionConsultRequestOptions) {
    if (!options?.suppressLegacyErrorModal) {
      this.carbonModalService.openCarbonCreditGetListError();
    }

    return this.handleErrorService.handleError(err as never);
  }
}
